import { useEffect, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import {
  getBillingSnapshot,
  startCheckout,
  openCustomerPortal,
  TIER_CATALOG,
  annualSavingGbp,
  type BillingCycle,
  type BillingSnapshot,
} from '../lib/billing';
import { getSeatUsage, tierLabel, type SeatUsage } from '../lib/team';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { isNativeApp } from '../lib/nativeShell';
import { useSession } from '../lib/auth';
import { IapPlans } from '../components/IapPlans';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function statusBadge(status: BillingSnapshot['status']): { text: string; bg: string; fg: string } {
  switch (status) {
    case 'active':    return { text: 'Active',         bg: '#E8F2EC', fg: '#16A34A' };
    case 'unpaid':    return { text: 'Not subscribed', bg: '#F1F5F9', fg: '#475569' };
    case 'trial':     return { text: 'Not subscribed', bg: '#F1F5F9', fg: '#475569' };
    case 'past_due':  return { text: 'Payment overdue', bg: '#FCE7E2', fg: '#B0413E' };
    case 'cancelled': return { text: 'Cancelled',      bg: '#F1F5F9', fg: '#475569' };
    case 'paused':    return { text: 'Paused',         bg: '#FFF8E5', fg: '#BF8E3D' };
    default:          return { text: 'Not subscribed', bg: '#F1F5F9', fg: '#475569' };
  }
}

export function Plan() {
  const { org } = useUserOrg();
  const [billing, setBilling] = useState<BillingSnapshot | null>(null);
  const [seats, setSeats] = useState<SeatUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const { session } = useSession();
  // Default to annual — it's the better deal for the customer and for us
  // (less churn, more cashflow upfront). Toggle lives at the top of the
  // tier cards so it's the first thing users see.
  const [cycle, setCycle] = useState<BillingCycle>('monthly');

  // True inside the iOS / Android Capacitor shells. Used to suppress every
  // Stripe upgrade and customer-portal affordance so we don't trip Apple's
  // Guideline 3.1.1 (in-app digital subscriptions must use IAP or hide the
  // payment UI entirely).
  const native = isNativeApp();

  useEffect(() => {
    if (!org) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [b, s] = await Promise.all([
          getBillingSnapshot(org.org_id),
          getSeatUsage(org.org_id),
        ]);
        if (!cancelled) { setBilling(b); setSeats(s); }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [org]);

  async function onUpgrade(tier: 'tier1' | 'tier2' | 'tier3') {
    setBusy(tier);
    setError(null);
    try {
      await startCheckout(tier, cycle);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  async function onPortal() {
    setBusy('portal');
    setError(null);
    try {
      await openCustomerPortal();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  if (!org || loading || !billing) {
    return (
      <AppShell title="Plan" back="/settings">
        <LoadingIndicator />
      </AppShell>
    );
  }

  const badge = statusBadge(billing.status);
  const isOnTier = (t: 'tier1' | 'tier2' | 'tier3') => billing.tier === t;
  const isEnterprise = billing.tier === 'enterprise';

  return (
    <AppShell title="Plan & billing" back="/settings">
      {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Current plan summary */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h3 style={{ margin: 0 }}>Current plan</h3>
          <span style={{
            background: badge.bg, color: badge.fg, padding: '4px 10px',
            borderRadius: 12, fontSize: 12, fontWeight: 600,
          }}>{badge.text}</span>
        </div>
        <p style={{ margin: '8px 0 0', fontSize: 18, fontWeight: 600 }}>
          {isEnterprise ? 'Enterprise (custom)' : tierLabel(billing.tier)}
        </p>
        {seats && (
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            {seats.used} of {seats.cap} seats used · {seats.pending} pending invites
          </p>
        )}
        {billing.currentPeriodEnd && billing.status === 'active' && (
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            {billing.cancelAtPeriodEnd
              ? `Cancels on ${fmtDate(billing.currentPeriodEnd)}`
              : `Renews on ${fmtDate(billing.currentPeriodEnd)}`}
          </p>
        )}
        {billing.status === 'past_due' && billing.gracePeriodEndsAt && (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--danger)' }}>
            Your last payment failed. Update your card by{' '}
            <strong>{fmtDate(billing.gracePeriodEndsAt)}</strong> to keep access.
          </p>
        )}
        {billing.referredByPartnerCode && (
          <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
            Referred by <strong style={{ color: 'var(--ink)' }}>{billing.referredByPartnerCode}</strong>
          </p>
        )}
      </div>

      {/* ============================================================
          Native shells (iOS / Android): reader-mode billing UI.
          Apple Guideline 3.1.1 forbids the in-app "Subscribe / Buy"
          flow that goes to a non-IAP checkout. The "reader" pattern
          DOES permit a single external link to the developer's own
          account-management page (Netflix, Spotify, Audible all do
          this). Primary action: a button that opens our web account
          page in the system browser. Secondary action: copy the URL
          for users who want to open it on a different device.
          ============================================================ */}
      {native ? (
        // Apple Guideline 3.1.1 (rejection 22 Sep 2026): inside the iOS app
        // the ONLY way to buy or change a plan is In-App Purchase. No links
        // or URLs to our website / Stripe. Reviewers also use this screen
        // (Settings → Plan & billing) to find and test the IAP products.
        <>
          <h3 className="section-title">Plans</h3>
          <IapPlans
            userId={session?.user.id}
            currentTier={billing.tier}
            onPurchased={() => window.location.reload()}
          />
        </>
      ) : (
        <>
      {/* Manage existing subscription */}
      {billing.stripeCustomerId && (
        <button
          type="button"
          className="btn btn-ghost"
          style={{ width: '100%', marginBottom: 16 }}
          disabled={busy === 'portal'}
          onClick={onPortal}
        >
          {busy === 'portal' ? 'Opening…' : 'Manage billing (cards, invoices, cancel) →'}
        </button>
      )}

      {/* Tier cards */}
      <h3 className="section-title">Plans</h3>

      {/* Monthly | Annual toggle. Annual = pay for 10 months, get 12.
          Defaults to annual because (a) it's the better deal for the
          customer and (b) it's better for us — less churn, cashflow
          upfront. The "Save £X" pill next to Annual quantifies the
          discount for the highest tier so the value pops without needing
          to hover or read fine print. */}
      <div
        role="tablist"
        aria-label="Billing cycle"
        className="rams-trade-chips"
        style={{ marginBottom: 12 }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={cycle === 'monthly'}
          className={`rams-trade-chip ${cycle === 'monthly' ? 'active' : ''}`}
          onClick={() => setCycle('monthly')}
        >
          Monthly
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={cycle === 'annual'}
          className={`rams-trade-chip ${cycle === 'annual' ? 'active' : ''}`}
          onClick={() => setCycle('annual')}
        >
          Annual <span style={{ opacity: 0.85, fontWeight: 500 }}>· 2 months free</span>
        </button>
      </div>

      {(['tier1', 'tier2', 'tier3'] as const).map((t) => {
        const cat = TIER_CATALOG[t];
        const current = isOnTier(t);
        const isAnnual = cycle === 'annual';
        // Annual headline price is per-month effective (yearlyGbp / 12)
        // with the total annual cost shown small below. This matches how
        // every other SaaS pricing page does it (Linear, Notion, Vercel)
        // — users compare £/month vs £/month and see the saving below.
        const headlinePerMonth = isAnnual ? Math.round(cat.yearlyGbp / 12) : cat.monthlyGbp;
        const saving = annualSavingGbp(t);
        return (
          <div key={t} className="card" style={{
            marginBottom: 10,
            borderColor: current ? 'var(--success, #2F6B47)' : undefined,
            borderWidth: current ? 2 : 1,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <p style={{ margin: 0, fontWeight: 600 }}>
                {cat.label}
                {current && <span style={{
                  marginLeft: 8, fontSize: 11, color: 'var(--success, #2F6B47)',
                }}>· current plan</span>}
              </p>
              <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                £{headlinePerMonth}<span style={{ fontSize: 13, fontWeight: 400 }}>/month</span>
              </p>
            </div>
            <p className="muted" style={{ margin: '4px 0 8px', fontSize: 13 }}>
              {cat.phones} · unlimited sites, sub-contractors, sign-ins
            </p>
            {isAnnual && (
              <p style={{
                margin: '0 0 8px',
                fontSize: 12,
                color: 'var(--success, #2F6B47)',
                fontWeight: 600,
              }}>
                £{cat.yearlyGbp}/year billed once · save £{saving}
              </p>
            )}
            {!current && (
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: '100%', fontSize: 14 }}
                disabled={busy === t}
                onClick={() => onUpgrade(t)}
              >
                {busy === t
                  ? 'Opening checkout…'
                  : `Switch to ${cat.label}${isAnnual ? ' — annual' : ''}`}
              </button>
            )}
          </div>
        );
      })}

      {/* Enterprise */}
      <div className="card" style={{ marginTop: 8, background: 'var(--bg-soft, #FAF7F2)' }}>
        <p style={{ margin: 0, fontWeight: 600 }}>Enterprise (26+ phones)</p>
        <p className="muted" style={{ margin: '4px 0 8px', fontSize: 13 }}>
          Custom pricing, dedicated support, security &amp; compliance review for larger contractors.
        </p>
        <a
          href="mailto:hello@safeandsorted.com?subject=Enterprise%20plan%20enquiry"
          className="btn btn-ghost"
          style={{ width: '100%', textAlign: 'center', textDecoration: 'none', fontSize: 14 }}
        >
          Get in touch →
        </a>
      </div>

      {/* Why we need this */}
      <p className="muted" style={{ fontSize: 11, marginTop: 24, lineHeight: 1.5 }}>
        Payments handled by Stripe. We don't see or store your card details.
        You can cancel any time from the "Manage billing" link above — no phone calls.
      </p>
        </>
      )}
    </AppShell>
  );
}
